/**
 * FilterPopoverButton Component
 *
 * A reusable popover filter button with consistent styling across the application.
 * Displays filter options in a popover when clicked, with badge showing active filter count.
 *
 * Usage:
 * - Pass filter controls as children
 * - Provide activeFilterCount to show badge
 * - Optionally provide onClearAll handler for "Clear all" button
 * - Customize popover width and alignment as needed
 */

import React from 'react';
import { T } from 'gt-react';
import { SlidersHorizontalIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface FilterPopoverButtonProps {
  /** Content to display inside the popover (filter controls) */
  children: React.ReactNode;
  /** Number of active filters (shows badge if > 0) */
  activeFilterCount?: number;
  /** Optional handler for "Clear all" button */
  onClearAll?: () => void;
  /** Width of the popover content (e.g., "400px", "600px") */
  popoverWidth?: string;
  /** Alignment of the popover relative to trigger button */
  popoverAlign?: 'start' | 'center' | 'end';
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Custom button text (defaults to "Filters") */
  buttonText?: string;
}

export function FilterPopoverButton({
  children,
  activeFilterCount = 0,
  onClearAll,
  popoverWidth = '400px',
  popoverAlign = 'start',
  isLoading = false,
  buttonText = 'Filters',
}: FilterPopoverButtonProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="default" disabled={isLoading}>
          <SlidersHorizontalIcon className="h-4 w-4 mr-2" />
          {buttonText}
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={`w-[${popoverWidth}]`}
        align={popoverAlign}
        style={{ width: popoverWidth }}
      >
        <div className="space-y-4">
          {/* Header with optional Clear All button */}
          <div className="flex items-center justify-between">
            {/* <h4 className="font-medium text-sm">Filter Options</h4> */}
            {onClearAll && activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-xs h-7"
              >
                <T>Clear all</T>
              </Button>
            )}
          </div>

          {/* Filter controls passed as children */}
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}
